import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { invitationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Users, User, Ticket, CheckCircle, UserPlus } from 'lucide-react';

const ROLE_INFO = {
  head_coach: { label: 'Head Coach', icon: Shield, description: 'Full access, can manage users' },
  coordinator: { label: 'Coordinator', icon: Users, description: 'Full coaching access' },
  trainer: { label: 'Trainer', icon: Users, description: 'Full access, injury focus' },
  asst_coach: { label: 'Asst Coach', icon: User, description: 'Limited access' },
  coach: { label: 'Coach', icon: User, description: 'Basic access' },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regRole, setRegRole] = useState('coach');
  
  // Invitation code
  const [inviteCode, setInviteCode] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  // Check URL for invitation code
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setInviteCode(code);
      setActiveTab('register');
      validateInvitation(code);
    }
  }, [searchParams]);

  const validateInvitation = async (code) => {
    if (!code || code.length < 6) return;
    
    setValidatingCode(true);
    try {
      const res = await invitationsAPI.validate(code);
      setInviteData(res.data);
      setRegName(res.data.name);
      setRegEmail(res.data.email);
      setRegRole(res.data.role);
      toast.success('Invitation code validated!');
    } catch (error) {
      setInviteData(null);
      if (code.length >= 8) {
        toast.error('Invalid or expired invitation code');
      }
    } finally {
      setValidatingCode(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!inviteData) {
      toast.error('You need a valid invitation code to register. Contact your Head Coach.');
      return;
    }
    
    setLoading(true);
    try {
      await register(regEmail, regPassword, regName, regRole, inviteCode.toUpperCase());
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const RoleIcon = inviteData ? ROLE_INFO[inviteData.role]?.icon || User : User;

  return (
    <div className="login-bg min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF3B30] mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-4xl text-white tracking-tight">T.O.T</h1>
          <p className="text-[#A1A1AA] mt-2">Football Team Management</p>
        </div>

        {/* Auth Card */}
        <div className="bg-[#121212] border border-[#27272A] p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#0A0A0A] border border-[#27272A]">
              <TabsTrigger 
                value="login" 
                className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white"
                data-testid="login-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white"
                data-testid="register-tab"
              >
                Register
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-[#A1A1AA]">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="coach@team.com"
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#FF3B30] focus:ring-[#FF3B30]"
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-[#A1A1AA]">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter password"
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#FF3B30] focus:ring-[#FF3B30]"
                    data-testid="login-password-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-semibold btn-press"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Invitation Code Input */}
                <div>
                  <Label htmlFor="invite-code" className="text-[#A1A1AA] flex items-center gap-2">
                    <Ticket className="w-4 h-4" />
                    Invitation Code
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="invite-code"
                      type="text"
                      value={inviteCode}
                      onChange={(e) => {
                        const code = e.target.value.toUpperCase();
                        setInviteCode(code);
                        if (code.length >= 8) {
                          validateInvitation(code);
                        } else {
                          setInviteData(null);
                        }
                      }}
                      placeholder="Enter invitation code"
                      className={`bg-[#0A0A0A] border-[#27272A] text-white uppercase tracking-wider ${
                        inviteData ? 'border-[#32D74B] pr-10' : ''
                      }`}
                      data-testid="invite-code-input"
                      maxLength={8}
                    />
                    {inviteData && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#32D74B]" />
                    )}
                  </div>
                  {validatingCode && (
                    <p className="text-xs text-[#71717A] mt-1">Validating code...</p>
                  )}
                  {!inviteData && !validatingCode && (
                    <p className="text-xs text-[#71717A] mt-1">
                      Contact your Head Coach for an invitation code
                    </p>
                  )}
                </div>

                {/* Show validated invitation details */}
                {inviteData && (
                  <div className="bg-[#32D74B]/10 border border-[#32D74B]/30 p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#32D74B]/20 flex items-center justify-center">
                        <RoleIcon className="w-5 h-5 text-[#32D74B]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{inviteData.name}</p>
                        <p className="text-sm text-[#A1A1AA]">
                          {ROLE_INFO[inviteData.role]?.label || inviteData.role} • {inviteData.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Name (pre-filled from invitation) */}
                <div>
                  <Label htmlFor="reg-name" className="text-[#A1A1AA]">Full Name</Label>
                  <Input
                    id="reg-name"
                    type="text"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="John Smith"
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#FF3B30] focus:ring-[#FF3B30]"
                    data-testid="register-name-input"
                    disabled={!!inviteData}
                    required
                  />
                </div>

                {/* Email (pre-filled from invitation) */}
                <div>
                  <Label htmlFor="reg-email" className="text-[#A1A1AA]">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="coach@team.com"
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#FF3B30] focus:ring-[#FF3B30]"
                    data-testid="register-email-input"
                    disabled={!!inviteData}
                    required
                  />
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="reg-password" className="text-[#A1A1AA]">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Create password"
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#FF3B30] focus:ring-[#FF3B30]"
                    data-testid="register-password-input"
                    required
                  />
                </div>

                {/* Role (shown but disabled if invitation exists) */}
                {inviteData && (
                  <div>
                    <Label className="text-[#A1A1AA]">Assigned Role</Label>
                    <div className="mt-2 bg-[#0A0A0A] border border-[#27272A] p-3 flex items-center gap-3">
                      <RoleIcon className="w-5 h-5 text-[#FF3B30]" />
                      <div>
                        <p className="text-white font-medium">{ROLE_INFO[regRole]?.label || regRole}</p>
                        <p className="text-xs text-[#71717A]">{ROLE_INFO[regRole]?.description}</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-semibold btn-press"
                  disabled={loading || !inviteData}
                  data-testid="register-submit-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>

                {!inviteData && (
                  <p className="text-center text-sm text-[#71717A]">
                    Registration requires an invitation from the Head Coach
                  </p>
                )}
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
